/* iceberg-ui */
import { Box, Flyout } from '@auviknetworks/iceberg-ui';

/* MUI */
import { Divider, Typography, useTheme } from '@mui/material';

/* Shared */
import { FontSizes, Z_INDEX } from 'application/Shared/Constants';
import { debug } from 'application/Shared/Utils';

/* Hooks */
import { useReportParams } from 'hooks/useReportParams';

/* React */
import { TFunction } from 'i18next';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/* Shared */
import {
    buildSubReportsSelection,
    buildSubSubReportsSelection
} from './helpers';
import { allReportsConfig } from './internal/allReportsConfig';

interface NavigationSection {
    id: string;
    title: string;
    description: string;
}

interface ReportNavigationFlyoutProps {
    onClose: () => void;
    onNavigateToSection?: (sectionId: string) => void;
    currentTab?: string;
    onTabChange?: (tab: string) => void;
}

/**
 * Gets the section title that appears in the report setup page, and the description for each section
 */
const getReportSectionTitleAndDescription = (
    reportType: string,
    subReportType: string,
    subSubReport: string,
    t: TFunction<'translation', undefined>
): { title: string; description: string } => {
    // Use the same translation keys that are used in the report setup page checkboxes
    const title =
        t(
            `Reports.${reportType}.checkbox.${subReportType}.subOption.${subSubReport}`
        ) || subSubReport;

    // Convert PascalCase to camelCase for translation key
    const camelCaseSubSubReport =
        subSubReport.charAt(0).toLowerCase() + subSubReport.slice(1);

    const descriptionKey = `Reports.${reportType}Report.${camelCaseSubSubReport}.description`;
    const description =
        t(descriptionKey) !== descriptionKey ? t(descriptionKey) : '';
    return { description, title };
};

/**
 * Generates a unique section ID by combining report type, tab type and sub-option key
 *
 * @param reportType - The type of report (e.g. 'SecurityReport', 'ComplianceReport')
 * @param tabType - The type of tab within the report (e.g. 'Overview', 'Details')
 * @param subOptionKey - The key identifying a specific sub-option/section
 * @returns A concatenated string that serves as a unique section identifier
 */
const generateSectionId = (
    reportType: string,
    tabType: string,
    subOptionKey: string
) => `${reportType}${tabType}${subOptionKey}`;

/**
 * Scrolls the viewport to the specified section of the report.
 *
 * This function attempts to smoothly scroll the page to bring the specified section into view.
 * If the section element cannot be found in the DOM, a warning is logged.
 *
 * @param sectionId - The DOM ID of the section to scroll to
 */
const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);

    if (element) {
        element.scrollIntoView({
            behavior: 'smooth',
            // Scroll to the start of the section
            block: 'start'
        });
    } else {
        debug.w('Could not find section with ID:', sectionId);
    }
};

/**
 * Navigation flyout component that allows users to jump between report sections.
 *
 * This component provides a navigation panel that lists all available report sections
 * with descriptions, allowing users to easily navigate to different parts of the report
 * and understand what each section contains.
 *
 * @param onClose - Function to call when the flyout should be closed
 * @param onNavigateToSection - Function to call when a section is selected for navigation
 * @param currentTab - The currently active tab
 * @param onTabChange - Function to call when a tab is selected
 */
export const ReportNavigationFlyout = ({
    onClose,
    onNavigateToSection,
    onTabChange
}: ReportNavigationFlyoutProps) => {
    const { t } = useTranslation();
    const { reportType } = useParams();
    const parsedReportParams = useReportParams(reportType);

    // Build navigation sections based on report configuration
    const sections = useMemo(() => {
        if (!parsedReportParams?.reportSelections || !reportType) return [];

        const { reportSelections, type } = parsedReportParams;

        // Check if this report has tabs (sub-reports)
        const subReports = buildSubReportsSelection({ reportSelections, type });

        const navigationSections: NavigationSection[] = [];

        if (subReports && subReports.length > 1) {
            // This report has tabs - show all tabs and all their sections
            subReports.forEach((subReport) => {
                // Add tab as a navigation item
                navigationSections.push({
                    description: '',
                    id: `tab_${subReport.type}`,
                    title: t(`Reports.${type}.checkbox.${subReport.type}`)
                });

                // Always add all sections for this tab
                if (subReport.subOptions) {
                    subReport.subOptions.forEach((subOption) => {
                        const sectionId = generateSectionId(
                            reportType,
                            subReport.type,
                            subOption.key
                        );

                        // If that subOption was selected/checked by the user
                        if (reportSelections.includes(sectionId)) {
                            const { title, description } =
                                getReportSectionTitleAndDescription(
                                    reportType,
                                    subReport.type,
                                    subOption.key,
                                    t
                                );

                            navigationSections.push({
                                description,
                                id: sectionId,
                                title
                            });
                        }
                    });
                }
            });
        } else {
            // This report doesn't have tabs - show sections directly
            const subSubReports = buildSubSubReportsSelection({
                reportSelections,
                type: reportType
            });

            if (subSubReports) {
                subSubReports.forEach((subReport) => {
                    subReport.subSubReports.forEach((subSubReport) => {
                        const sectionId = generateSectionId(
                            reportType,
                            subReport.type,
                            subSubReport
                        );

                        const { title, description } =
                            getReportSectionTitleAndDescription(
                                reportType,
                                subReport.type,
                                subSubReport,
                                t
                            );
                        navigationSections.push({
                            description,
                            id: sectionId,
                            title
                        });
                    });
                });
            }
        }

        return navigationSections;
    }, [parsedReportParams, reportType, t]);

    /**
     * Handles navigation to different sections or tabs within the report.
     *
     * - Tabs: Switch the active tab view (no scrolling needed)
     * - Sections: Scroll to specific content within the current tab
     *
     * @param sectionId - The ID of the section or tab to navigate to
     */
    const handleNavigateToSection = useCallback(
        (sectionId: string) => {
            // Check if this is a tab navigation
            if (sectionId.startsWith('tab_')) {
                const tabName = sectionId.replace('tab_', '');
                // Switch to the tab
                onTabChange?.(tabName);
                // Update current section to the tab
                onNavigateToSection?.(sectionId);
                // Scroll to the top of the report content
                const reportsExportedMainContainer = document.getElementById(
                    'reportsExportedMainContainer'
                );
                if (reportsExportedMainContainer) {
                    reportsExportedMainContainer.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                } else {
                    window.scrollTo({ behavior: 'smooth', top: 0 });
                }
            } else {
                // Regular section navigation
                onNavigateToSection?.(sectionId);

                // Check if the section exists in the DOM
                const element = document.getElementById(sectionId);
                if (element) {
                    scrollToSection(sectionId);
                } else {
                    // Section not found, it might be in a different tab
                    // Find the correct tab for this section using the report config
                    const reportConfig = allReportsConfig.find(
                        (config) => config.type === reportType
                    );
                    if (reportConfig && reportConfig.checkboxes) {
                        const matchingCheckbox = reportConfig.checkboxes.find(
                            (checkbox) =>
                                checkbox.subOptions?.some(
                                    (subOption) =>
                                        reportType &&
                                        generateSectionId(
                                            reportType,
                                            checkbox.type,
                                            subOption.key
                                        ) === sectionId
                                )
                        );
                        if (matchingCheckbox) {
                            onTabChange?.(matchingCheckbox.type);
                            // Wait for tab switch then scroll
                            setTimeout(() => {
                                scrollToSection(sectionId);
                            }, 100);
                        }
                    }
                }
            }
        },
        [onNavigateToSection, onTabChange, reportType]
    );

    const theme = useTheme();

    return (
        <Flyout
            data-testid="reportNavigationFlyout"
            onClose={onClose}
            title={t('Reports.navigation')}
            headerIcon="List"
            width="480px"
            zIndex={Z_INDEX.FLYOUT_LOW}
        >
            <Box padding="16px">
                <Typography
                    sx={{
                        fontSize: FontSizes[1],
                        marginBottom: '16px'
                    }}
                >
                    {t('Reports.navigationDescription')}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {sections.map((section, index) => (
                        <React.Fragment key={section.id}>
                            <Box
                                sx={{
                                    '&:hover': {
                                        backgroundColor:
                                            theme.palette.menuItem
                                                .hoverBackgroundColor
                                    },
                                    backgroundColor: 'transparent',
                                    borderRadius: '8px',
                                    cursor: 'pointer'
                                }}
                                padding="12px 16px"
                                marginBottom="8px"
                                onClick={() =>
                                    handleNavigateToSection(section.id)
                                }
                            >
                                <Typography
                                    sx={{
                                        fontSize: section.id.startsWith('tab_')
                                            ? FontSizes[3]
                                            : FontSizes[2],
                                        fontWeight: section.id.startsWith(
                                            'tab_'
                                        )
                                            ? 400
                                            : 600,
                                        marginBottom: section.description
                                            ? '8px'
                                            : 0
                                    }}
                                    color={
                                        section.id.startsWith('tab_')
                                            ? 'text.primary'
                                            : 'text.tertiary'
                                    }
                                >
                                    {section.title}
                                </Typography>
                                {section.description && (
                                    <Typography
                                        sx={{
                                            fontSize: FontSizes[1]
                                        }}
                                    >
                                        {section.description}
                                    </Typography>
                                )}
                            </Box>
                            {index < sections.length - 1 &&
                                !section.id.startsWith('tab_') &&
                                sections[index + 1]?.id.startsWith('tab_') && (
                                    <Divider
                                        sx={{
                                            borderColor: 'border.darkerDivider',
                                            borderWidth: '1px'
                                        }}
                                    />
                                )}
                        </React.Fragment>
                    ))}
                </Box>
            </Box>
        </Flyout>
    );
};
